import { TableToolTipProps } from "@/components/Table/table.interface";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import * as React from "react";

/**
 * Long enough that the tooltip doesn't flash at everything the pointer crosses
 * on its way somewhere else — a header row is a line of adjacent targets — and
 * short enough that pausing on a column reads as asking a question.
 * `enterNextDelay` matches it so moving along the row re-arms the wait instead
 * of firing instantly on the second header.
 */
export const COLUMN_TOOLTIP_ENTER_DELAY_MS = 500;

/**
 * The explanation hangs off the column header itself rather than off an ⓘ
 * beside it. The icon was a second thing in every one of these cells competing
 * with the label for a fixed column width, and on narrow columns (OSI is 80px,
 * Weight is 9%) the label and the icon together were most of it.
 *
 * It does cost discoverability: nothing now advertises that a column has an
 * explanation. The dotted underline is what is left to hint at it, which is why
 * the delay matters — with no icon to aim at, people find these by resting on a
 * header they don't understand.
 */
export default function ColumnHeaderTooltip({
  title,
  children,
  toolTipProps,
}: ColumnHeaderTooltipProps) {
  return (
    <Tooltip
      placement="top"
      enterDelay={COLUMN_TOOLTIP_ENTER_DELAY_MS}
      enterNextDelay={COLUMN_TOOLTIP_ENTER_DELAY_MS}
      // Defaults, so a caller's `toolTipProps` can still override the delays.
      // Written after the spread they silently beat anything passed, which is
      // the "prop that quietly does nothing" this API's own docs claim to have
      // designed out.
      {...toolTipProps}
      // The title is not negotiable: it comes from the header.
      title={title}
      // Without this MUI puts `aria-label={title}` on the wrapper — which
      // CONTAINS the visible label, so the column's accessible name becomes the
      // explanation and "OSI" is never announced. Voice control ("click OSI")
      // stops matching, and every cell's column announcement changes.
      // `describeChild` emits `aria-describedby` instead, which is the right
      // relationship for an explanation of something already named.
      describeChild
    >
      {/* Tooltip has to hand a ref and the pointer handlers to one element, and
          a bare label may be a string. */}
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          // A dotted underline rather than `cursor: help`. The cursor was
          // invisible on any sortable header: TableSortLabel is a ButtonBase
          // that sets `cursor: pointer` and covers the label exactly, so
          // nothing of the wrapper showed through. Pointer is also the right
          // cursor there — the header IS clickable — so the hint has to be
          // something other than the cursor.
          textDecoration: "underline dotted",
          textUnderlineOffset: "3px",
          textDecorationColor: "currentcolor",
          textDecorationThickness: "1px",
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
}

interface ColumnHeaderTooltipProps {
  title: NonNullable<React.ReactNode>;
  children: React.ReactNode;
  toolTipProps?: TableToolTipProps;
}
